// Stickers on the page (docs/stickers-plan.md D552–D554, D559, D565, D571, D572,
// D575): the `<echo-sticker>` element and `window.EchoSticker`, shared by both clients —
// the desktop inlines it into the head of every window, the web imports it at
// the entry. The engine and the scheduler are `sticker-worker.js`.
//
//     <echo-sticker src file poster format mode play-key label aspect active>
//
//   * `src` — the sticker's bytes (`/v1/stickers/{fid}` on the web, the media
//     protocol's `/media/s/{fid}` on the desktop); `file` — its file id, the
//     key everything here is kept by; `poster` — its poster's address, the
//     file itself for a still sticker.
//   * `format` — `tgs` plays; `image` is a still sticker (D564): the poster and
//     nothing else, ever.
//   * `mode` — `once` in the feed, a pack's link card included (D575): it
//     plays once when it has been at least 60 % visible for 150 ms and
//     nothing pauses the feed, and again on hover (D552); once per chat entry
//     (`play-key`, D565) — played meaning played to its last frame, which
//     from then on stands in its poster's place (D572). `shown` in the panel,
//     the marketplace and a pack's sheet (D571): the feed's «once» — the same
//     sight, the same last frame held, again on hover or the `active`
//     attribute (keyboard focus) — but every time it comes back into view,
//     at 30 fps at most, and standing only while the window is not active:
//     the feed's covers are what it is drawn on.
//   * `aspect` — `W/H` of the composition, from the attachment or the pack's
//     row, so the canvas is fitted without waiting for the engine. The host's
//     size is the client's to set: it is `contain: strict`, and so a box that
//     never moves the layout around it.
//   * `label` — what a screen reader says: `role="img"` and the label go
//     through `ElementInternals`, so nothing is written into the light DOM.
//   * `gone` — the words that stand in the sticker's place when its poster
//     cannot be had: a pack taken down answers 404 for its files (D551), and
//     the sticker says so instead of drawing a broken picture.
//
// At rest the shadow root holds one `<img>` and no canvas: no worker, zero
// CPU. Activating puts a fresh canvas of the fitted size under the poster,
// hands it to a worker (`transferControlToOffscreen`) and hides the poster one
// frame after the worker has drawn. A sticker that played once — `once` or
// `shown` — keeps its last frame, and goes back to its `<img>` only off
// screen, so the swap is never seen: the poster, or for a played feed
// sticker its last frame, which its worker hands over as a picture (D572). A
// feed sticker remounted while it draws — its bubble replaced — hands its
// canvas to the newcomer and goes on. Nothing in here mutates the light DOM,
// at rest or per frame — the feed's keepers and `ink.js` watch it.
//
//     EchoSticker.configure({ workerUrl, wasmUrl, workers, still, blobWorker, activity })
//     EchoSticker.pause(reason, on)     // 'panel' | 'sheet' | 'viewer': the feed's (D554)
//     EchoSticker.chatEntered(channel)  // «once» starts over (D565)
//     EchoSticker.poster(src, box)      // -> Promise<{ blob, width, height }>
(function () {
  "use strict";

  if (typeof window === "undefined" || window.EchoSticker) return;

  // D553's starting values; docs/stickers-plan.md «The numbers».
  var CEILING = 8;
  var FEED_DELAY_MS = 150;
  var VISIBLE = 0.6;
  var WATCHDOG_MS = 500;
  var MAX_SIDE = 512;
  var PLAYED_MAX = 4096;
  var BYTES_BUDGET = 4 * 1024 * 1024;
  // D572: a few hundred last frames at the feed's size.
  var LAST_BUDGET = 8 * 1024 * 1024;

  var config = {
    workerUrl: null,
    wasmUrl: null,
    workers: 1,
    still: false,
    // The desktop starts its workers from a Blob URL (no CSP there, and its
    // custom protocol is the page's to fetch from, never a worker's); the
    // web's CSP allows workers from its own origin only.
    blobWorker: false,
    // Off on a test page that has no window to be active in, and in the
    // desktop's floating layer: it is shown only while a card is up in it,
    // and holds the focus only when that card asks for the keyboard (D571).
    activity: true,
  };

  var modulePromise = null;
  var workerUrlPromise = null;
  var pool = []; // index -> slot
  var deny = new Set(); // files that stay posters for the session
  var played = new Map(); // play-key -> true, oldest first: played to the end
  var lastFrames = new Map(); // file -> { url, side, size }, oldest first (D572)
  var lastTotal = 0;
  var reasons = new Set(); // the client's pause reasons
  var feedPaused = false; // a reason, or the window not active
  var inactive = false; // the window not active
  var queue = []; // stickers seen and waiting under the ceiling
  var running = new Set(); // stickers drawing now
  var bytes = new Map(); // src -> ArrayBuffer, oldest first
  var bytesTotal = 0;
  var fetches = new Map(); // src -> Promise<ArrayBuffer>
  var byId = new Map(); // animation id -> element
  var visible = new Set(); // feed stickers at least 60 % on screen
  var posterWaits = new Map(); // job id -> { resolve, reject, slot }
  var statWaits = new Map(); // job id -> { resolve, slot }
  var nextId = 1;
  var watchdog = 0;
  var watchedAt = 0; // the watchdog's last look
  var awakeAt = 0; // since when the page has been there to see its workers
  var unnamed = []; // slot index -> the files a kill that named nobody found drawing
  var io = null;

  // ---------------------------------------------------------------------
  // The pool: workers per window, made when first needed (D553).

  function workerUrl() {
    if (!workerUrlPromise) {
      workerUrlPromise = config.blobWorker
        ? fetch(config.workerUrl)
            .then(function (r) {
              if (!r.ok) throw new Error("sticker worker " + r.status);
              return r.text();
            })
            .then(function (source) {
              return URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
            })
        : Promise.resolve(config.workerUrl);
      // A failed fetch is tried again by the next worker, as the engine's is.
      workerUrlPromise.catch(function () {
        workerUrlPromise = null;
      });
    }
    return workerUrlPromise;
  }

  // Compiled once per window, on the main thread, and posted to every
  // worker: a worker never compiles, and never fetches.
  function wasmModule() {
    if (!modulePromise) {
      modulePromise = fetch(config.wasmUrl)
        .then(function (r) {
          if (!r.ok) throw new Error("tlottie.wasm " + r.status);
          return r.arrayBuffer();
        })
        .then(function (b) {
          return WebAssembly.compile(b);
        });
      modulePromise.catch(function () {
        modulePromise = null;
      });
    }
    return modulePromise;
  }

  function makeSlot(index) {
    var slot = {
      index: index,
      worker: null,
      ready: null,
      running: false,
      lastBeat: 0,
      suspect: null,
      anims: new Set(),
    };
    slot.ready = Promise.all([wasmModule(), workerUrl()]).then(function (got) {
      return new Promise(function (resolve, reject) {
        var w = new Worker(got[1]);
        slot.worker = w;
        slot.resolve = resolve;
        w.onmessage = function (event) {
          onWorker(slot, event.data);
        };
        w.onerror = function (event) {
          reject(event.message || "sticker worker failed");
        };
        // With the pauses as they stand: one said before this worker was
        // here never reached it.
        w.postMessage({ t: "init", module: got[0], feed: feedPaused, window: inactive });
      });
    });
    slot.ready.catch(function () {
      if (pool[slot.index] === slot) pool[slot.index] = null;
    });
    return slot;
  }

  function hash(text) {
    var h = 5381;
    for (var i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
    return h >>> 0;
  }

  // The same file always goes to the same worker, which keeps its parsed
  // instances and its bytes (D553).
  function slotFor(file) {
    var n = Math.max(1, config.workers);
    var index = hash(file) % n;
    if (!pool[index]) pool[index] = makeSlot(index);
    return pool[index];
  }

  function onWorker(slot, m) {
    slot.lastBeat = performance.now();
    var el;
    switch (m.t) {
      case "ready":
        slot.resolve();
        break;
      case "beat":
        slot.running = m.running;
        slot.suspect = null;
        if (m.running) armWatchdog();
        break;
      case "risky":
        slot.suspect = m.file;
        break;
      case "first":
        el = byId.get(m.id);
        if (el) el._first(m.id);
        break;
      case "ended":
        el = byId.get(m.id);
        if (el) el._ended(m.id);
        break;
      case "last":
        keepLast(m.file, m.blob, m.side);
        break;
      case "failed":
        deny.add(m.file);
        slot.anims.delete(m.id);
        el = byId.get(m.id);
        if (el) el._failed(m.id);
        break;
      case "lost":
        // Stopped in the worker for nothing its file did — its bytes let go
        // of before a parse it needed (D658): back at rest, to start again
        // with them, as `retire` does with a worker's stickers.
        slot.anims.delete(m.id);
        el = byId.get(m.id);
        if (el) {
          el._release(false);
          if (el.isConnected) el._arm();
        }
        break;
      case "bloated":
        // Let go only idle: no sticker on it, and no poster asked of it.
        if (slot.anims.size === 0 && !postersOn(slot)) retire(slot, false);
        break;
      case "poster":
        var wait = posterWaits.get(m.id);
        posterWaits.delete(m.id);
        if (!wait) break;
        if (m.error) wait.reject(new Error(m.error));
        else wait.resolve({ blob: m.blob, width: m.width, height: m.height, composition: m.composition });
        break;
      case "stats":
        var asked = statWaits.get(m.id);
        statWaits.delete(m.id);
        if (asked) asked.resolve(m.stats);
        break;
    }
  }

  // A worker silent for ~500 ms while it has work is stopped and replaced;
  // the file it said it was about to draw stays a poster for the session.
  // Only silence the page was there to see counts (D615): a hidden
  // document's workers may get no frame to tick on, and timers that stood
  // still — the page throttled, the machine asleep — saw nothing either; the
  // ~500 ms start again from when the page is back.
  function armWatchdog() {
    if (watchdog) return;
    watchedAt = performance.now();
    watchdog = setInterval(function () {
      var now = performance.now();
      if (document.hidden || now - watchedAt > WATCHDOG_MS) awakeAt = now;
      watchedAt = now;
      var busy = false;
      for (var i = 0; i < pool.length; i++) {
        var slot = pool[i];
        if (!slot || !slot.running) continue;
        busy = true;
        if (now - Math.max(slot.lastBeat, awakeAt) > WATCHDOG_MS) retire(slot, true);
      }
      if (!busy) {
        clearInterval(watchdog);
        watchdog = 0;
      }
    }, WATCHDOG_MS / 2);
  }

  function retire(slot, killed) {
    if (pool[slot.index] === slot) pool[slot.index] = null;
    if (slot.worker) slot.worker.terminate();
    // Nothing is posted to it again: a start whose bytes were still coming
    // finds it gone and goes to the next worker (D553).
    slot.worker = null;
    if (killed) judge(slot);
    var orphans = [];
    slot.anims.forEach(function (id) {
      var el = byId.get(id);
      if (el) orphans.push(el);
    });
    slot.anims.clear();
    // A poster asked of it is never answered: refused, which the editors
    // take for a file that does not fit (D549).
    posterWaits.forEach(function (wait, id) {
      if (wait.slot !== slot) return;
      posterWaits.delete(id);
      wait.reject(new Error("sticker worker replaced"));
    });
    // Nor the harness's question: no stats from a worker that is gone.
    statWaits.forEach(function (wait, id) {
      if (wait.slot !== slot) return;
      statWaits.delete(id);
      wait.resolve(null);
    });
    orphans.forEach(function (el) {
      el._release(false);
      if (el.isConnected) el._arm();
    });
  }

  // Whom a kill denies (D553, D614): the file the worker said it was
  // about to draw. With none said — a stop it could not announce, in a frame
  // it had drawn in time before or outside the engine — every file drawing
  // on it that was drawing at its previous kill too, which named nobody
  // either: such a file costs its viewers two kills, not one a second for
  // as long as it is in sight.
  function judge(slot) {
    if (slot.suspect) {
      deny.add(slot.suspect);
      unnamed[slot.index] = null;
      return;
    }
    var drawing = new Set();
    slot.anims.forEach(function (id) {
      var el = byId.get(id);
      // One holding its last frame draws nothing, and nor does one that
      // stands paused (a sheet over the feed, the window inactive).
      if (el && el._state !== "holding" && !el._stands()) drawing.add(el._file());
    });
    var before = unnamed[slot.index];
    if (before) {
      drawing.forEach(function (file) {
        if (before.has(file)) deny.add(file);
      });
    }
    unnamed[slot.index] = drawing;
  }

  // Whether a poster (D549) is being made by this worker.
  function postersOn(slot) {
    var asked = false;
    posterWaits.forEach(function (wait) {
      if (wait.slot === slot) asked = true;
    });
    return asked;
  }

  function broadcast(message) {
    for (var i = 0; i < pool.length; i++) {
      var slot = pool[i];
      if (slot && slot.worker) slot.worker.postMessage(message);
    }
  }

  // ---------------------------------------------------------------------
  // Bytes: fetched by the page, cached by size, handed over as copies.

  function loadBytes(src) {
    if (bytes.has(src)) {
      var kept = bytes.get(src);
      bytes.delete(src);
      bytes.set(src, kept);
      return Promise.resolve(kept.slice(0));
    }
    if (!fetches.has(src)) {
      fetches.set(
        src,
        fetch(src)
          .then(function (r) {
            if (!r.ok) throw new Error("sticker " + r.status);
            return r.arrayBuffer();
          })
          .then(
            function (buf) {
              fetches.delete(src);
              bytes.set(src, buf);
              bytesTotal += buf.byteLength;
              bytes.forEach(function (b, key) {
                if (bytesTotal > BYTES_BUDGET && key !== src) {
                  bytes.delete(key);
                  bytesTotal -= b.byteLength;
                }
              });
              return buf;
            },
            function (e) {
              fetches.delete(src);
              throw e;
            }
          )
      );
    }
    return fetches.get(src).then(function (buf) {
      return buf.slice(0);
    });
  }

  // ---------------------------------------------------------------------
  // Last frames (D572): what a played feed sticker shows at rest instead of
  // its poster, one picture per file — the largest one seen.

  function keepLast(file, blob, side) {
    var held = lastFrames.get(file);
    if (held && held.side >= side) return;
    var gone = [];
    if (held) {
      lastFrames.delete(file);
      lastTotal -= held.size;
      gone.push(held);
    }
    lastFrames.set(file, { url: URL.createObjectURL(blob), side: side, size: blob.size });
    lastTotal += blob.size;
    lastFrames.forEach(function (kept, key) {
      if (lastTotal > LAST_BUDGET && key !== file) {
        lastFrames.delete(key);
        lastTotal -= kept.size;
        gone.push(kept);
      }
    });
    settleLast(gone);
  }

  // Every sticker on what it should now show — this file's new picture, or
  // its poster where a let-go one was, and a sticker in sight left with
  // neither armed to make it again — before a let-go address is revoked, so
  // no `<img>` is left on a dead one.
  function settleLast(gone) {
    everySticker(function (el) {
      if (!el._img) return;
      el._showPoster();
      el._arm();
    });
    gone.forEach(function (kept) {
      URL.revokeObjectURL(kept.url);
    });
  }

  // ---------------------------------------------------------------------
  // The ceiling, the queue and the pauses

  // Stickers counting against the ceiling: everything drawing, except what
  // stands — a pause frees the ceiling for a sheet over the feed (D554).
  function active() {
    var n = 0;
    running.forEach(function (el) {
      if (!el._stands()) n++;
    });
    return n;
  }

  function dequeue() {
    if (queue.length === 0) return;
    // In feed order, so the queue plays top to bottom as a reader reads.
    queue.sort(function (a, b) {
      return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    // What stands keeps its place; a sheet's stickers go on past the feed's
    // (D571).
    var standing = [];
    while (queue.length > 0 && active() < CEILING) {
      var el = queue.shift();
      if (el._stands()) {
        standing.push(el);
        continue;
      }
      el._queued = false;
      if (!el.isConnected || !visible.has(el) || el._state !== "rest") continue;
      var how = el._wants();
      if (how) el._start(how);
    }
    queue = standing.concat(queue);
  }

  function computePaused() {
    var away = config.activity && (document.hidden || !document.hasFocus());
    var paused = reasons.size > 0 || away;
    if (paused === feedPaused && away === inactive) return;
    feedPaused = paused;
    inactive = away;
    broadcast({ t: "pause", feed: paused, window: away });
    if (!paused || !away) {
      visible.forEach(function (el) {
        el._arm();
      });
      dequeue();
    }
  }

  document.addEventListener("visibilitychange", computePaused);
  window.addEventListener("focus", computePaused);
  window.addEventListener("blur", computePaused);
  // The watchdog's ~500 ms count from the document's return (D615).
  document.addEventListener("visibilitychange", function () {
    if (!document.hidden) awakeAt = performance.now();
  });

  function observer() {
    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            entry.target._seen(entry);
          });
        },
        { threshold: [0, VISIBLE] }
      );
    }
    return io;
  }

  // ---------------------------------------------------------------------
  // The element

  // The canvas is the box's size and contains its frame as the poster does,
  // so a frame held while the box changes size follows it (D659).
  var STYLE =
    ":host{display:inline-block;position:relative;contain:strict;line-height:0}" +
    "img,canvas{position:absolute;inset:0;margin:auto;width:100%;height:100%;object-fit:contain;pointer-events:none}" +
    "img{-webkit-user-drag:none;user-select:none}" +
    ".gone{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;" +
    "box-sizing:border-box;padding:8px;text-align:center;font-size:12px;line-height:16px;" +
    "color:var(--labels-tertiary);border:1px dashed var(--separators-non-opaque);border-radius:12px}";

  class EchoStickerElement extends HTMLElement {
    static get observedAttributes() {
      return ["src", "poster", "format", "mode", "label", "active", "file", "gone", "play-key"];
    }

    constructor() {
      super();
      this._id = 0;
      this._slot = null;
      this._canvas = null;
      this._img = null;
      this._ratio = 0;
      this._rect = null;
      this._timer = 0;
      this._hovered = false;
      this._queued = false;
      this._state = "rest"; // rest | starting | playing | holding | failed
      this._observed = false;
      this._goneNote = null;
      this._internals = this.attachInternals ? this.attachInternals() : null;
      if (this._internals) this._internals.role = "img";
      var self = this;
      this.addEventListener("pointerenter", function () {
        self._hover(true);
      });
      this.addEventListener("pointerleave", function () {
        self._hover(false);
      });
    }

    connectedCallback() {
      if (!this.shadowRoot) {
        var root = this.attachShadow({ mode: "open" });
        var style = document.createElement("style");
        style.textContent = STYLE;
        var img = document.createElement("img");
        img.alt = "";
        img.draggable = false;
        img.loading = "lazy";
        img.decoding = "async";
        root.appendChild(style);
        root.appendChild(img);
        this._img = img;
        var self = this;
        img.addEventListener("error", function () {
          // A last frame let go under it (D572): the poster, not the words.
          var poster = self.getAttribute("poster") || "";
          if (poster && img.getAttribute("src") !== poster) {
            img.setAttribute("src", poster);
            return;
          }
          // A picture that failed does not say why, and a gateway with too much
          // in work is not a sticker removed (docs/admission.md D1075): once
          // more, after any wait it names has passed, before the words say so.
          var src = img.getAttribute("src");
          if (src && self._askedAgain !== src) {
            self._askedAgain = src;
            setTimeout(function () {
              if (self.isConnected && img.getAttribute("src") === src) {
                img.setAttribute("src", src);
              }
            }, 8000);
            return;
          }
          self._gone(true);
        });
        img.addEventListener("load", function () {
          self._askedAgain = null;
          self._gone(false);
        });
      }
      this._refresh();
    }

    disconnectedCallback() {
      this._unobserve();
      this._disarm();
      this._dequeueSelf();
      if (!this._id) {
        this._state = "rest";
        return;
      }
      // Drawing as its bubble is remounted — the gateway's answer to a
      // sticker just sent takes the bubble's place on the desktop — it hands
      // its animation to the bubble's new sticker instead of leaving it on
      // frame 0 (D572). Looked at in a microtask: whichever of the two the
      // client moved first, both are where they will be by then, and nothing
      // has been painted in between.
      var self = this;
      queueMicrotask(function () {
        self._leave();
      });
    }

    _leave() {
      // Moved inside the document, not gone: it keeps everything.
      if (this.isConnected || !this._id) return;
      var heir = this._heir();
      if (heir) this._handOver(heir);
      else this._release(true);
      this._state = "rest";
    }

    // The sticker that took this one's place: connected, a feed sticker of
    // the same key and file, not started.
    _heir() {
      var key = this.getAttribute("play-key");
      if (!key || this._mode() !== "once") return null;
      var file = this._file();
      var src = this.getAttribute("src");
      var found = null;
      document.querySelectorAll("echo-sticker[play-key]").forEach(function (el) {
        if (found || el._id || el._state !== "rest" || !el._img) return;
        if (el.getAttribute("play-key") !== key || el._mode() !== "once") return;
        if (el._file() !== file || el.getAttribute("src") !== src) return;
        found = el;
      });
      return found;
    }

    // The canvas moves into the heir's shadow root, and the worker draws on
    // as it did: a canvas handed to a worker keeps its frame and its link
    // when moved (D572).
    _handOver(heir) {
      var id = this._id;
      heir._disarm();
      heir._dequeueSelf();
      heir._id = id;
      heir._slot = this._slot;
      heir._state = this._state;
      heir._canvas = this._canvas;
      byId.set(id, heir);
      if (running.delete(this)) running.add(heir);
      heir.shadowRoot.insertBefore(heir._canvas, heir._img);
      // Its poster goes as this one's did: at once if a frame is drawn,
      // otherwise one frame after the first.
      if (heir._state !== "starting") heir._img.style.visibility = "hidden";
      this._id = 0;
      this._slot = null;
      this._canvas = null;
      if (this._img) this._img.style.visibility = "";
    }

    attributeChangedCallback(name, before, after) {
      if (before === after || !this._img) return;
      if (name === "src" || name === "file" || name === "format" || name === "mode") {
        this._release(true);
        this._state = "rest";
      }
      if (name === "active") {
        this._hover(after !== null);
        return;
      }
      if (name === "gone") {
        if (this._goneNote) this._goneNote.textContent = after || "";
        return;
      }
      this._refresh();
    }

    _mode() {
      return this.getAttribute("mode") === "shown" ? "shown" : "once";
    }

    // Whether it may not start or go on now: the feed's for every pause
    // (D554), the panel's and a sheet's only while the window is not active
    // (D571).
    _stands() {
      return this._mode() === "shown" ? inactive : feedPaused;
    }

    // The poster could not be had (D551): the words of `gone` in its place,
    // inside the shadow root like everything else here.
    _gone(on) {
      var words = this.getAttribute("gone");
      if (on && words) {
        if (!this._goneNote) {
          this._goneNote = document.createElement("span");
          this._goneNote.className = "gone";
          this.shadowRoot.appendChild(this._goneNote);
        }
        this._goneNote.textContent = words;
        this._img.style.visibility = "hidden";
      } else if (this._goneNote) {
        this._goneNote.remove();
        this._goneNote = null;
        this._img.style.visibility = "";
      }
    }

    _file() {
      return this.getAttribute("file") || this.getAttribute("src") || "";
    }

    _refresh() {
      this._showPoster();
      if (this._internals) this._internals.ariaLabel = this.getAttribute("label") || null;
      var onSight = this.isConnected && this._animates();
      if (onSight && !this._observed) {
        observer().observe(this);
        this._observed = true;
      } else if (!onSight) {
        this._unobserve();
      }
      // A sticker in sight whose file changed under it starts again: the
      // observer says nothing while it stays in sight.
      this._arm();
    }

    // What its `<img>` shows: the poster — or, for a feed sticker played to
    // its end since the chat was entered, its last frame where one is kept
    // (D572).
    _posterNow() {
      var poster = this.getAttribute("poster") || "";
      var key = this.getAttribute("play-key");
      if (this._mode() !== "once" || !key || !played.has(key)) return poster;
      var file = this._file();
      var held = lastFrames.get(file);
      if (!held) return poster;
      // Not a picture far smaller than this sticker — kept by a reply's
      // square or a link card of the same file — drawn across it: the poster,
      // until sight draws the last frame again at this size (D616). The
      // size is the one the observer last gave: asked for here, it would
      // force a layout.
      var fit = this._rect && this._rect.width > 0 ? this._fit() : null;
      if (fit && held.side * 2 < Math.max(fit.w, fit.h)) return poster;
      lastFrames.delete(file);
      lastFrames.set(file, held);
      return held.url;
    }

    _showPoster() {
      var src = this._posterNow();
      if (this._img.getAttribute("src") !== src) this._img.setAttribute("src", src);
    }

    _unobserve() {
      if (this._observed && io) io.unobserve(this);
      this._observed = false;
      visible.delete(this);
    }

    // Whether this sticker ever animates in this window: a TGS, a window
    // with workers, motion allowed (D559), and not a file that failed.
    _animates() {
      return (
        this.getAttribute("format") === "tgs" &&
        !config.still &&
        config.workers > 0 &&
        !!config.wasmUrl &&
        !deny.has(this._file())
      );
    }

    // What sight may start now: `play`, from the first frame — a sticker
    // that has not played to its end since the chat was entered (D565, D572),
    // or one with no `play-key`, which plays on every sight (D571) —; `end`,
    // straight to its last frame — a played one whose last frame is not kept
    // as a picture at least its size (D572, D616) —; or nothing.
    _wants() {
      if (!this._animates() || this._goneNote) return "";
      var key = this.getAttribute("play-key");
      if (!key || !played.has(key)) return "play";
      var held = lastFrames.get(this._file());
      var fit = held ? this._fit() : null;
      return held && (!fit || held.side >= Math.max(fit.w, fit.h)) ? "" : "end";
    }

    _seen(entry) {
      // An entry queued before `unobserve` may still come (Gecko): a sticker
      // that left the document is not seen, nor kept in `visible`.
      if (!this._observed || !this.isConnected) return;
      this._ratio = entry.intersectionRatio;
      this._rect = entry.boundingClientRect;
      if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE) {
        visible.add(this);
        this._arm();
      } else {
        visible.delete(this);
        this._disarm();
        this._dequeueSelf();
        // Back to its `<img>` only off screen, so the swap is never seen.
        if (!entry.isIntersecting) this._release(true);
      }
    }

    // The feed's 150 ms: on screen long enough to be looked at, not crossed
    // by a scroll.
    _arm() {
      if (this._state !== "rest" || this._timer) return;
      if (!visible.has(this) || this._stands() || !this._wants()) return;
      var self = this;
      this._timer = setTimeout(function () {
        self._timer = 0;
        if (!self.isConnected || !visible.has(self) || self._stands() || self._state !== "rest") return;
        var how = self._wants();
        if (!how) return;
        if (active() >= CEILING) {
          if (!self._queued) {
            self._queued = true;
            queue.push(self);
          }
          return;
        }
        self._start(how);
      }, FEED_DELAY_MS);
    }

    _disarm() {
      if (this._timer) clearTimeout(this._timer);
      this._timer = 0;
    }

    _dequeueSelf() {
      if (!this._queued) return;
      this._queued = false;
      var at = queue.indexOf(this);
      if (at >= 0) queue.splice(at, 1);
    }

    // Hovering plays it again, first in the worker's order (D552, D571).
    _hover(on) {
      this._hovered = on;
      if (!this._animates()) return;
      if (on && this._state === "holding" && this._slot) {
        running.add(this);
        this._state = "playing";
        this._slot.worker.postMessage({ t: "replay", id: this._id });
        this._slot.worker.postMessage({ t: "priority", id: this._id, priority: 0 });
      } else if (on && this._state === "rest") {
        this._disarm();
        this._dequeueSelf();
        this._start("play");
      } else if (this._id && this._slot && this._slot.worker) {
        this._slot.worker.postMessage({ t: "priority", id: this._id, priority: on ? 0 : 1 });
      }
    }

    // The canvas and the worker (D553), from the first frame (`play`) or at
    // the last (`end`, D572).
    _start(how) {
      var file = this._file();
      var src = this.getAttribute("src");
      if (!src || !file) return;
      var fit = this._fit();
      if (!fit) return;
      var mode = this._mode();
      // A feed sticker hands its last frame back as a picture, unless the
      // page keeps one at least as large (D572).
      var held = lastFrames.get(file);
      var keep =
        mode === "once" && !!this.getAttribute("play-key") && !(held && held.side >= Math.max(fit.w, fit.h));
      this._dequeueSelf();
      var id = nextId++;
      var slot = slotFor(file);
      this._id = id;
      this._slot = slot;
      this._state = "starting";
      running.add(this);
      byId.set(id, this);

      var canvas = document.createElement("canvas");
      canvas.width = fit.w;
      canvas.height = fit.h;
      this.shadowRoot.insertBefore(canvas, this._img);
      this._canvas = canvas;
      var offscreen = canvas.transferControlToOffscreen();
      Promise.all([slot.ready, loadBytes(src)]).then(
        function (got) {
          // Whoever has the animation now: a remount may have handed it over
          // while the bytes came (D572).
          var owner = byId.get(id);
          if (!owner) return;
          // Its worker let go while the bytes came — killed, or replaced for
          // its memory: back at rest, to start on the next one, as `retire`
          // does with the stickers it had (D553).
          if (!slot.worker || pool[slot.index] !== slot) {
            owner._release(false);
            if (owner.isConnected) owner._arm();
            return;
          }
          slot.anims.add(id);
          slot.worker.postMessage(
            {
              t: "start",
              id: id,
              file: file,
              bytes: got[1],
              canvas: offscreen,
              w: fit.w,
              h: fit.h,
              // The panel and the sheets at 30 fps at most (D552, D571).
              capped: mode === "shown",
              stand: mode === "shown" ? "window" : "feed",
              end: how === "end",
              keep: keep,
              priority: owner._hovered ? 0 : 1,
            },
            [got[1], offscreen]
          );
        },
        function () {
          // Not fetched — offline, say, which the desktop answers with a 404
          // as it does everything else —, and not a broken file: back at
          // rest, it tries again at its next sight (D572).
          var owner = byId.get(id);
          if (owner) owner._release(false);
        }
      );
    }

    // The composition fitted into the host's box, keeping its aspect — the
    // engine stretches it otherwise — at the device's pixels, 512 at most on
    // the long side (D553).
    _fit() {
      var rect = this._rect && this._rect.width > 0 ? this._rect : this.getBoundingClientRect();
      if (!(rect.width > 0 && rect.height > 0)) return null;
      var aspect = (this.getAttribute("aspect") || "1/1").split("/");
      var aw = parseFloat(aspect[0]) || 1;
      var ah = parseFloat(aspect[1]) || 1;
      var s = Math.min(rect.width / aw, rect.height / ah);
      var dpr = window.devicePixelRatio || 1;
      var w = aw * s * dpr;
      var h = ah * s * dpr;
      var long = Math.max(w, h);
      if (long > MAX_SIDE) {
        w = (w * MAX_SIDE) / long;
        h = (h * MAX_SIDE) / long;
      }
      return {
        w: Math.max(1, Math.round(w)),
        h: Math.max(1, Math.round(h)),
      };
    }

    _first(id) {
      if (id !== this._id) return;
      this._state = "playing";
      var self = this;
      // One frame after the worker drew: the canvas is on screen before the
      // poster goes, so there is never an empty frame between them.
      requestAnimationFrame(function () {
        if (self._id === id && self._img) self._img.style.visibility = "hidden";
      });
    }

    _ended(id) {
      if (id !== this._id) return;
      this._state = "holding";
      // Played is played to the end (D572): one left before its last frame
      // plays again at its next sight.
      var key = this.getAttribute("play-key");
      if (key && this._mode() === "once") {
        played.delete(key);
        played.set(key, true);
        if (played.size > PLAYED_MAX) played.delete(played.keys().next().value);
        // Under the canvas, for when it goes: the last frame if one is kept
        // already; the worker's picture of it comes otherwise.
        this._showPoster();
      }
      running.delete(this);
      dequeue();
    }

    _failed(id) {
      if (id !== this._id) return;
      this._release(false);
      this._state = "failed";
    }

    // Back to its `<img>` — the poster, or a played feed sticker's last frame
    // (D572). `tell` is false when the worker is gone already.
    _release(tell) {
      if (this._id) {
        byId.delete(this._id);
        if (this._slot) {
          this._slot.anims.delete(this._id);
          if (tell && this._slot.worker) this._slot.worker.postMessage({ t: "stop", id: this._id });
        }
      }
      this._id = 0;
      this._slot = null;
      if (this._canvas) {
        this._canvas.remove();
        this._canvas = null;
      }
      if (this._img) {
        this._showPoster();
        if (!this._goneNote) this._img.style.visibility = "";
      }
      if (running.delete(this)) dequeue();
      if (this._state !== "failed") this._state = "rest";
    }
  }

  customElements.define("echo-sticker", EchoStickerElement);

  function everySticker(fn) {
    document.querySelectorAll("echo-sticker").forEach(fn);
  }

  window.EchoSticker = {
    configure: function (options) {
      var wasStill = config.still;
      for (var k in options) {
        if (Object.prototype.hasOwnProperty.call(options, k) && options[k] !== undefined) {
          config[k] = options[k];
        }
      }
      if (config.still && !wasStill) {
        everySticker(function (el) {
          el._release(true);
          el._refresh();
        });
      } else if (!config.still && wasStill) {
        everySticker(function (el) {
          el._refresh();
        });
      }
      computePaused();
    },

    pause: function (reason, on) {
      if (on) reasons.add(reason);
      else reasons.delete(reason);
      computePaused();
    },

    // A chat entry (D565): everything plays once more. Stickers already on
    // screen that are resting go back from their last frames to their
    // posters (D572) and start again; the ones holding a last frame on a
    // canvas are the previous chat's and leave with it.
    chatEntered: function () {
      played.clear();
      everySticker(function (el) {
        if (el._img) el._showPoster();
      });
      visible.forEach(function (el) {
        el._arm();
      });
    },

    // Frame 0 as a poster (D549), by the same engine the feed plays with.
    poster: function (src, box) {
      return loadBytes(src).then(function (buf) {
        var slot = slotFor("poster:" + src);
        return slot.ready.then(function () {
          return new Promise(function (resolve, reject) {
            var id = nextId++;
            // Kept with its worker, which `retire` refuses it for: a worker
            // killed or replaced never answers.
            posterWaits.set(id, { resolve: resolve, reject: reject, slot: slot });
            // `file` is what the watchdog denies should this poster stop the
            // worker: the web editor's staged sticker is keyed by its `src`.
            slot.worker.postMessage({ t: "poster", id: id, file: src, bytes: buf, box: box || 320 }, [buf]);
          });
        });
      });
    },

    // For the harness: what each worker has done, and what the page holds.
    _debug: {
      // The workers that answer: one let go of before it did is left out.
      stats: function () {
        var asks = [];
        for (var i = 0; i < pool.length; i++) {
          var slot = pool[i];
          if (!slot || !slot.worker) continue;
          asks.push(
            new Promise(function (resolve) {
              var id = nextId++;
              statWaits.set(id, { resolve: resolve, slot: slot });
              slot.worker.postMessage({ t: "stats", id: id });
            })
          );
        }
        return Promise.all(asks).then(function (all) {
          return all.filter(Boolean);
        });
      },
      slots: function () {
        return pool.filter(Boolean).map(function (slot) {
          return { index: slot.index, running: slot.running, lastBeat: slot.lastBeat, animations: slot.anims.size };
        });
      },
      page: function () {
        return {
          running: running.size,
          active: active(),
          queued: queue.length,
          visible: visible.size,
          played: played.size,
          lastFrames: lastFrames.size,
          lastBytes: lastTotal,
          denied: Array.from(deny),
          paused: feedPaused,
          inactive: inactive,
          workers: pool.filter(Boolean).length,
        };
      },
      config: config,
      // The last frame kept for `file` (D572), or null.
      last: function (file) {
        var held = lastFrames.get(file);
        return held ? { url: held.url, side: held.side, size: held.size } : null;
      },
      // Every last frame let go, as the budget would (D572).
      dropLast: function () {
        var gone = Array.from(lastFrames.values());
        lastFrames.clear();
        lastTotal = 0;
        settleLast(gone);
      },
    },
  };
})();
